/** Escape a string for safe literal use inside a RegExp. */
function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^\${}()|[\]\\]/g, '\\$&');
}

/**
 * Build the `propagateTraceHeaderCorsUrls` matcher list for the browser tracer.
 *
 * Returns a single origin-anchored, boundary-terminated regex so the W3C
 * `traceparent` header is attached ONLY to cross-origin requests to our own API
 * origin — never to third-party hosts (which would leak internal trace IDs and can
 * break their CORS preflight). Same-origin requests are always propagated by OTEL
 * regardless of this list. Returns [] (no cross-origin propagation) when the origin
 * is unset or malformed — the secure default.
 */
export function buildApiPropagationMatchers(apiOrigin: string | undefined): RegExp[] {
  if (!apiOrigin || !apiOrigin.trim()) return [];
  let origin: string;
  try {
    origin = new URL(apiOrigin).origin;
  } catch {
    return [];
  }
  if (origin === 'null') return [];
  // Anchor at the origin and require a path/query/fragment boundary or end-of-string,
  // so "https://api.example.com.evil.com" does NOT match "https://api.example.com".
  return [new RegExp('^' + escapeRegExp(origin) + '(?:[/?#]|$)', 'i')];
}

/**
 * Build the `propagateContextUrls` list for @vercel/otel's server-side fetch
 * instrumentation (Path A), so the W3C `traceparent` header is injected on the
 * Next server → Go API hop and the FE→BE→Postgres trace stitches into one Tempo
 * trace.
 *
 * CRITICAL: @vercel/otel matches string entries with `fullRequestUrl.startsWith(entry)`,
 * where the request URL includes the scheme (e.g. `https://api.example.com/v1/x`).
 * A bare host (`api.example.com`, i.e. `new URL(base).host`) is therefore NEVER a
 * prefix and silently disables propagation in production — masked in local dev only
 * because @vercel/otel unconditionally propagates to http+localhost. We return the
 * URL *origin* (`https://api.example.com`), a true prefix of every backend request.
 * Returns [] when the base URL is unset or malformed — the secure default (no
 * propagation, never a startup crash).
 */
export function buildServerPropagationUrls(apiBaseUrl: string | undefined): string[] {
  if (!apiBaseUrl || !apiBaseUrl.trim()) return [];
  try {
    const { origin } = new URL(apiBaseUrl);
    return origin === 'null' ? [] : [origin];
  } catch {
    return [];
  }
}
